import React, { FC, useEffect, useState } from 'react';
import { alpha, Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { ChargeWalletModal } from './charge-wallet.modal';
import { addCommas } from '@persian-tools/persian-tools';

type WalletCardProps = {
    amount?: number;
    isLoading: boolean;
    id?: number;
    title: string;
    name: string;
    navigatedOpen?: boolean;
    onMoreData: (wallet: number) => void;
};

const walletMeta: Record<
    string,
    { icon: string; description: string }
> = {
    sms: {
        icon: 'fa-light fa-message-sms',
        description: 'اعتبار مورد استفاده برای ارسال پیامک‌های سامانه',
    },
    wage: {
        icon: 'fa-light fa-coins',
        description: 'اعتبار مورد استفاده برای کارمزد تراکنش ها',
    },
};

const WalletCard: FC<WalletCardProps> = (props): JSX.Element => {
    const {
        amount,
        isLoading,
        id,
        title,
        name,
        navigatedOpen,
        onMoreData,
    } = props;
    const [modalOpen, setOpenModal] = useState<boolean>(false);
    const meta = walletMeta[name] ?? walletMeta.wage;

    useEffect(() => {
        if (navigatedOpen && id) {
            setOpenModal(true);
        }
    }, [id, navigatedOpen]);

    return (
        <>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 220,
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    boxShadow:
                        theme.palette.mode === 'light'
                            ? `0 12px 34px ${alpha(theme.palette.common.black, 0.055)}`
                            : 'none',
                })}
            >
                <Box sx={{ p: { xs: 2.25, md: 2.75 }, flex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={(theme) => ({
                                    width: 44,
                                    height: 44,
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                    borderRadius: 2.25,
                                    color:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.12 : 0.08,
                                    ),
                                    border: `1px solid ${alpha(
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.2 : 0.1,
                                    )}`,
                                })}
                            >
                                <i className={meta.icon} style={{ fontSize: 19 }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    className="font-yekan-heavy"
                                    sx={{ fontSize: 16, lineHeight: 1.6 }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.8,
                                        display: 'block',
                                    }}
                                >
                                    {meta.description}
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                        >
                            موجودی فعلی
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            {isLoading ? (
                                <Skeleton width={120} height={38} />
                            ) : (
                                <Typography
                                    dir="ltr"
                                    sx={{
                                        fontFamily: 'IRANSans, Tahoma, Arial, sans-serif',
                                        fontSize: { xs: 25, md: 28 },
                                        fontWeight: 500,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {addCommas(amount ?? 0)}
                                </Typography>
                            )}
                            <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                ریال
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider />

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1px 1fr',
                        alignItems: 'stretch',
                        minHeight: 48,
                        backgroundColor: 'background.light',
                    }}
                >
                    <Button
                        disabled={!id}
                        onClick={() => id && onMoreData(id)}
                        variant="text"
                        startIcon={<i className="fa-light fa-chart-line" />}
                        sx={{ borderRadius: 0, color: 'text.primary' }}
                    >
                        گزارشات
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    <Button
                        disabled={!id}
                        onClick={() => setOpenModal(true)}
                        variant="text"
                        startIcon={<i className="fa-light fa-wallet" />}
                        sx={{ borderRadius: 0, color: 'text.primary' }}
                    >
                        افزایش اعتبار
                    </Button>
                </Box>
            </Box>

            <ChargeWalletModal
                modalOpen={modalOpen}
                onClose={() => setOpenModal(false)}
                id={id}
                title={title}
            />
        </>
    );
};

export { WalletCard };
